import React, { createContext, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Toaster } from '../ui/toaster';
import { toast as baseToast, useToast as useRadixToast } from '../../hooks/use-toast';

const ToastContext = createContext(null);

const DEFAULT_TITLES = {
  success: 'Success',
  error: 'Something went wrong',
  warning: 'Heads up',
  info: 'Update',
};

const VARIANT_MAP = {
  success: 'success',
  error: 'destructive',
  warning: 'warning',
  info: 'info',
};

const showToast = (type, message, options = {}) => {
  const {
    title,
    description,
    duration,
    toastProps = {},
  } = options;

  const handle = baseToast({
    title: title ?? DEFAULT_TITLES[type],
    description: description ?? message,
    variant: toastProps.variant ?? VARIANT_MAP[type] ?? 'info',
    duration: duration ?? (type === 'error' ? 5000 : 2800),
    ...toastProps,
  });

  return handle;
};

export const ToastProvider = ({ children }) => (
  <ToastContext.Provider value={true}>
    {children}
    <Toaster />
  </ToastContext.Provider>
);

ToastProvider.propTypes = {
  children: PropTypes.node,
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  const { dismiss } = useRadixToast();

  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const showSuccess = useCallback((message, options) => showToast('success', message, options), []);
  const showError = useCallback((message, options) => showToast('error', message, options), []);
  const showWarning = useCallback((message, options) => showToast('warning', message, options), []);
  const showInfo = useCallback((message, options) => showToast('info', message, options), []);

  const addToast = useCallback((options = {}) => {
    const type = options.type || 'info';
    return showToast(type, options.message || '', options);
  }, []);

  const removeToast = useCallback((id) => {
    if (id) {
      dismiss(id);
    } else {
      dismiss();
    }
  }, [dismiss]);

  const removeAllToasts = useCallback(() => dismiss(), [dismiss]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    addToast,
    removeToast,
    removeAllToasts,
  };
};

// Only named exports are available from this module
